import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export type RecordTab = {
  key: string;
  label: string;
  /** Shorter label for the phone tab strip. */
  shortLabel?: string;
  icon: ReactNode;
};

type InvestorRecordHeaderProps = {
  name: string;
  firmName?: string;
  clientSince?: string;
  actions?: ReactNode;
  tabs: RecordTab[];
  activeKey: string;
};

/**
 * The investor record hero and tab strip, mirroring the advisor portal's
 * investor page: name with firm and client-since on one line on the left, actions on the right, and
 * one tab per record section underneath.
 */
const InvestorRecordHeader = ({
  name,
  firmName,
  clientSince,
  actions,
  tabs,
  activeKey,
}: InvestorRecordHeaderProps) => (
  <div className="investor-record">
    <header className="investor-record-hero">
      <div className="investor-record-identity">
        <h1 className="investor-record-name">{name}</h1>
        {(firmName || clientSince) && (
          <p className="investor-record-firm">
            {[firmName && `Client of ${firmName}`, clientSince && `Client since ${clientSince}`]
              .filter(Boolean)
              .join(' · ')}
          </p>
        )}
      </div>
      {actions && <div className="investor-record-actions">{actions}</div>}
    </header>
    <nav className="investor-record-tabs" aria-label="Record">
      {tabs.map((tab) => {
        const isActive = tab.key === activeKey;
        return (
          <Link
            key={tab.key}
            to={tab.key}
            className={`investor-record-tab${isActive ? ' active' : ''}${
              tab.shortLabel ? ' has-short-label' : ''
            }`}
            aria-current={isActive ? 'page' : undefined}
            // Phones show the short label, so name the tab explicitly.
            aria-label={tab.label}
          >
            <span className="investor-record-tab-icon" aria-hidden="true">
              {tab.icon}
            </span>
            <span className="investor-record-tab-label" aria-hidden="true">
              {tab.label}
            </span>
            {tab.shortLabel && (
              <span className="investor-record-tab-label-short" aria-hidden="true">
                {tab.shortLabel}
              </span>
            )}
          </Link>
        );
      })}
    </nav>
  </div>
);

export default InvestorRecordHeader;
